import { apiBuilder } from "@zodios/core";
import { z } from "zod";

export const registrationApiDefinitions = apiBuilder({
  method: "post",
  path: "/api/registerorganization/",
  alias: "registerOrganization",
  parameters: [
    {
      name: "body",
      type: "Body",
      schema: z.object({
        first_name: z.string(),
        last_name: z.string(),
        email: z.string().email(),
        password: z.string(),
        organization_name: z.string(),
        contact_number: z.string(),
        hear_about_us: z.string().nullable(),
        terms_agreement: z.boolean(),
        signup_source: z.string().nullable(),
        language: z.string().optional(),
      }),
    },
  ],
  response: z.unknown(),
})
  .addEndpoint({
    method: "post",
    path: "/api/verify_email/confirm/",
    alias: "confirmEmail",
    parameters: [{ name: "body", type: "Body", schema: z.object({ token: z.string() }) }],
    response: z.object({ status: z.literal("success") }),
  })
  .addEndpoint({
    method: "post",
    path: "/api/verify_email/resend/",
    alias: "resendVerificationEmail",
    response: z.object({ status: z.literal("success") }),
  })
  .addEndpoint({
    method: "get",
    path: "/api/mfa/setup/",
    alias: "getMFACodeInfo",
    parameters: [],
    response: z.object({ otpauthurl: z.string(), secret: z.string(), digits: z.number(), issuer: z.string() }),
  })
  .addEndpoint({
    method: "post",
    path: "/api/mfa/setup/",
    alias: "confirmMFADeviceSetup",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          secret: z.string(),
          digits: z.number(),
          device_type: z.string(),
          otp: z.string(),
        }),
      },
    ],
    response: z.object({
      setup_complete: z.boolean(),
      recovery_tokens: z.array(z.string()).optional(),
    }),
  })
  .addEndpoint({
    method: "get",
    path: "/api/mfa/manage/",
    alias: "getMFADeviceManagement",
    parameters: [],
    response: z.object({
      username: z.string(),
      totp_devices: z
        .array(
          z.object({
            id: z.number(),
            name: z.string(),
            confirmed: z.boolean(),
            last_t: z.number(),
          }),
        )
        .optional(),
    }),
  })
  .addEndpoint({
    method: "delete",
    path: "/api/mfa/manage/",
    alias: "deleteMFADevice",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          totp_devices: z.array(z.object({ id: z.number() })),
        }),
      },
    ],
    response: z.object({
      info: z.string(),
      device_list: z
        .array(
          z.object({
            id: z.number(),
            deleted: z.boolean(),
          }),
        )
        .optional(),
    }),
  })
  .addEndpoint({
    method: "post",
    path: "/api/authentication/?custom_api_request=user_login_verification",
    alias: "fetchLoginUserVerification",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          custom_api_request: z.literal("user_login_verification"),
          user_email: z.string().email(),
        }),
      },
    ],
    response: z.string(),
  })
  .addEndpoint({
    method: "post",
    path: "/api/reset-password/",
    alias: "requestPasswordReset",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          email: z.string().email(),
        }),
      },
    ],
    response: z.unknown(),
  })
  .addEndpoint({
    method: "post",
    path: "/api/new-password/",
    alias: "processPasswordReset",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.union([
          z.object({
            token: z.string(),
            newPassword: z.string(),
          }),
          z.object({
            currentPassword: z.string().optional(),
            newPassword: z.string(),
          }),
        ]),
      },
    ],
    response: z.unknown(),
  })
  .addEndpoint({
    method: "post",
    path: "auth/convert-token",
    alias: "convertTokens",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          grant_type: z.literal("convert_token"),
          backend: z.string(),
          client_id: z.string(),
          client_secret: z.string(),
          token: z.string(),
        }),
      },
    ],
    response: z.object({
      access_token: z.string(),
      refresh_token: z.string(),
    }),
  })
  .addEndpoint({
    method: "post",
    path: "/auth/token",
    alias: "login",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          client_id: z.string(),
          client_secret: z.string(),
          grant_type: z.literal("password"),
          username: z.string().email(),
          password: z.string(),
          otp: z.string().optional(),
          recovery_code: z.string().optional(),
        }),
      },
    ],
    response: z
      .object({
        access_token: z.string(),
        refresh_token: z.string(),
        requires_mfa: z.literal(false).optional(),
      })
      .or(
        z.object({
          requires_mfa: z.literal(true),
        }),
      ),
  })
  .addEndpoint({
    method: "post",
    path: "/auth/passinvite",
    alias: "acceptUserInvitation",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          client_id: z.string(),
          client_secret: z.string(),
          grant_type: z.literal("password"),
          first_name: z.string(),
          last_name: z.string(),
          password: z.string(),
          inviteToken: z.string(),
        }),
      },
    ],
    response: z.object({
      status: z.string(),
      email_address: z.string(),
    }),
  })
  .build();
